import React from 'react';
import { Row, Col, FormGroup, Label } from 'reactstrap';
import { ConsiderationsInfo } from '../interfaces/considerations.interface';

// ============ TYPES ============
interface ConsiderationsQuotationComponentProps {
  considerationsInfo: ConsiderationsInfo;
  onConsiderationsChange: (field: keyof ConsiderationsInfo, value: string) => void;
}

// ============ SECTION COMPONENT ============
interface SectionProps {
  title: string;
  borderColor: string;
  children: React.ReactNode;
  backgroundColor?: string;
}

const Section: React.FC<SectionProps> = ({ 
  title, 
  borderColor, 
  children, 
  backgroundColor = '#f8f9fa' 
}) => (
  <div style={{
    border: '1px solid #dee2e6',
    borderRadius: '8px',
    padding: '20px',
    marginTop: '20px',
    backgroundColor
  }}>
    <h5 style={{
      marginBottom: '20px',
      color: '#495057',
      borderBottom: `2px solid ${borderColor}`,
      paddingBottom: '8px'
    }}>
      {title}
    </h5>
    {children}
  </div>
);

// ============ MAIN COMPONENT ============
const ConsiderationsQuotationComponent: React.FC<ConsiderationsQuotationComponentProps> = ({
  considerationsInfo,
  onConsiderationsChange,
}) => {
  const hasContent = considerationsInfo.summaryNotes && considerationsInfo.summaryNotes.trim() !== '';

  const defaultText = `Precio no incluye gastos legales de escrituración
Validez: Sujeto a disponibilidad.
Descuento de $1,000 aplicado al precio total por compra múltiple/ descuento por pronto pago, etc
Se reserva con $5,000 y documentos para Cumplimiento por inmueble y firmar PCV en plazo de 15 días después de reserva.
La cuota de mantenimiento la definirá la Administración previo al inicio de operaciones del edificio.`;

  const handleTextareaChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    onConsiderationsChange('summaryNotes', event.target.value);
  };

  const handleUseDefault = () => {
    onConsiderationsChange('summaryNotes', defaultText);
  };

  const handleClear = () => {
    onConsiderationsChange('summaryNotes', '');
  };

  return (
    <Section title="Consideraciones" borderColor="#28a745" backgroundColor="#fff">
      <Row>
        <Col md="12">
          <FormGroup>
            <Label className="form-control-label">
              📝 Notas y Consideraciones Adicionales
            </Label>
            <div style={{ marginBottom: '10px' }}>
              <button
                type="button"
                onClick={handleUseDefault}
                style={{
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  padding: '5px 10px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  marginRight: '10px',
                  cursor: 'pointer'
                }}
              >
                📋 Usar texto por defecto
              </button>
              <button
                type="button"
                onClick={handleClear}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '5px 10px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                🗑️ Limpiar
              </button>
            </div>
            <textarea
              className="form-control"
              rows={8}
              placeholder="Escriba aquí las consideraciones adicionales para la cotización..."
              value={considerationsInfo.summaryNotes}
              onChange={handleTextareaChange}
              style={{
                resize: 'vertical',
                fontFamily: 'inherit',
                fontSize: '14px',
                lineHeight: '1.4',
                ...(hasContent ? { 
                  color: '#000000', 
                  fontWeight: '500',
                  backgroundColor: '#f8f9fa'
                } : {})
              }}
            />
            <small className="form-text text-muted">
              💡 <strong>Ejemplos:</strong> Precio no incluye gastos legales, validez sujeta a disponibilidad, 
              descuentos aplicados, condiciones de reserva, cuotas de mantenimiento, etc.
            </small>
            {hasContent && (
              <div style={{ 
                marginTop: '10px', 
                padding: '8px 12px', 
                backgroundColor: '#d1edff', 
                border: '1px solid #bee5eb', 
                borderRadius: '4px',
                fontSize: '12px',
                color: '#0c5460'
              }}>
                ✅ <strong>{considerationsInfo.summaryNotes.split('\n').length} líneas</strong> | 
                <strong> {considerationsInfo.summaryNotes.length} caracteres</strong> escritos
              </div>
            )}
          </FormGroup>
        </Col>
      </Row>
    </Section>
  );
};

export default ConsiderationsQuotationComponent;